/**
 * Small drawing helpers shared by the Fall props (flask, cracks, shards, boulder, gem, shield,
 * buoy). The frozen toon kit covers round subjects; props are mostly polygons and lines, so
 * these add polygon paths, rounded polygons, inked ropes and tapered strokes in the same style.
 */
import { INK } from "../palette.js";
import { inkStyle } from "../toon-kit.js";

/**
 * Add a closed polygon to the current path. Points are [x, y] in unit space, mapped by
 * `x * sx + ox`, `y * sy + oy`.
 * @param {CanvasRenderingContext2D} c
 * @param {number[][]} pts
 */
export function polyPath(c, pts, sx, sy, ox, oy) {
  for (var i = 0; i < pts.length; i++) {
    var x = pts[i][0] * sx + (ox || 0), y = pts[i][1] * sy + (oy || 0);
    if (i) c.lineTo(x, y); else c.moveTo(x, y);
  }
  c.closePath();
}

/**
 * Add a closed polygon with every corner rounded by up to `radius` (clamped to half of the
 * shorter neighbouring edge), so chunky rocks and badges keep soft cartoon corners.
 * @param {CanvasRenderingContext2D} c
 * @param {number[][]} pts absolute points
 * @param {number} radius
 */
export function roundedPolyPath(c, pts, radius) {
  var n = pts.length;
  for (var i = 0; i < n; i++) {
    var p = pts[i], prev = pts[(i + n - 1) % n], next = pts[(i + 1) % n];
    var lp = Math.hypot(p[0] - prev[0], p[1] - prev[1]);
    var ln = Math.hypot(next[0] - p[0], next[1] - p[1]);
    var r = Math.min(radius, lp * 0.5, ln * 0.5);
    var ax = p[0] + (prev[0] - p[0]) * (r / lp), ay = p[1] + (prev[1] - p[1]) * (r / lp);
    var bx = p[0] + (next[0] - p[0]) * (r / ln), by = p[1] + (next[1] - p[1]) * (r / ln);
    if (i) c.lineTo(ax, ay); else c.moveTo(ax, ay);
    c.quadraticCurveTo(p[0], p[1], bx, by);
  }
  c.closePath();
}

/**
 * Add a rounded rectangle to the current path. Same as ctx.roundRect, which older mobile
 * Safari lacks; a throw there would leave a baked picture half drawn.
 */
export function roundRectPath(c, x, y, w, h, r) {
  var rr = Math.max(0, Math.min(r, w / 2, h / 2));
  c.moveTo(x + rr, y);
  c.lineTo(x + w - rr, y);
  c.arcTo(x + w, y, x + w, y + rr, rr);
  c.lineTo(x + w, y + h - rr);
  c.arcTo(x + w, y + h, x + w - rr, y + h, rr);
  c.lineTo(x + rr, y + h);
  c.arcTo(x, y + h, x, y + h - rr, rr);
  c.lineTo(x, y + rr);
  c.arcTo(x, y, x + rr, y, rr);
  c.closePath();
}

/** Map unit points to absolute points (scale then offset). */
export function mapPts(pts, sx, sy, ox, oy) {
  return pts.map(function (p) { return [p[0] * sx + ox, p[1] * sy + oy]; });
}

/**
 * An inked rope: the current path stroked wide in ink, then narrower in `color` (Ghost
 * Circuit's fuse cord / hoop recipe), optionally with a thin lit line on top.
 */
export function inkedRope(c, inkW, color, colorW, lit, litW) {
  inkStyle(c, inkW, INK);
  c.stroke();
  c.strokeStyle = color;
  c.lineWidth = colorW;
  c.stroke();
  if (lit) {
    c.strokeStyle = lit;
    c.lineWidth = litW || colorW * 0.35;
    c.stroke();
  }
}

/**
 * Add a closed ribbon along a polyline whose width runs from `w0` at the first point to `w1`
 * at the last, so a crack is fat at the impact and ends in a sharp point. Filled, it keeps
 * crisp corners at every zig-zag (stroked segments would round them into bent pipes).
 * @param {CanvasRenderingContext2D} c
 * @param {number[][]} pts
 */
export function ribbonPath(c, pts, w0, w1) {
  var n = pts.length;
  var left = [], right = [];
  for (var i = 0; i < n; i++) {
    var p = pts[i];
    var a = pts[Math.max(0, i - 1)], b = pts[Math.min(n - 1, i + 1)];
    var dx = b[0] - a[0], dy = b[1] - a[1], dl = Math.hypot(dx, dy) || 1;
    var hw = (w0 + (w1 - w0) * (n > 1 ? i / (n - 1) : 0)) * 0.5;
    left.push([p[0] - (dy / dl) * hw, p[1] + (dx / dl) * hw]);
    right.push([p[0] + (dy / dl) * hw, p[1] - (dx / dl) * hw]);
  }
  c.moveTo(left[0][0], left[0][1]);
  for (var j = 1; j < n; j++) c.lineTo(left[j][0], left[j][1]);
  for (var k = n - 1; k >= 0; k--) c.lineTo(right[k][0], right[k][1]);
  c.closePath();
}
